import { useEffect } from "react";
import { WlReorderVariant } from "../../../../utils/wlReorderSubscriptionExperiment";
import arrowRightIcon from "../../../../icons/wl-reorder-arrow-right.svg";
import ReorderProductSummary from "./ReorderProductSummary";
import ReorderCarePlusPlanCard from "./ReorderCarePlusPlanCard";
import ReorderOneTimeOptionCard from "./ReorderOneTimeOptionCard";
import { useWlReorderPlanSelection } from "./useWlReorderPlanSelection";
import * as translator from "../../../../utils/translator";
import { CARE_PLUS_ORANGE_GRADIENT } from "../../../CarePlusShared/carePlusUi";

interface Props {
  reorderData: any;
  variant: WlReorderVariant;
  searchParams: URLSearchParams;
  country: string;
  language: string;
  setStickyNotificationDetails: any;
  setWlReorderPage?: (active: boolean) => void;
}

/** WL test arms B and C: Care+ monthly vs one-time (Figma 38938:177 / 285 / 582). */
const WlReorderPlanScreen = (props: Props) => {
  const { reorderData, searchParams, country, language, setWlReorderPage } = props;
  const { offer, pricing, medicationPrice, showOneTimeOption, selectedOption, setSelectedOption, submit, submitting } =
    useWlReorderPlanSelection(props);
  const product = reorderData.orderProductItems[0];
  const currencyRate = reorderData.currencyRate || 1;
  const text = (key: string) => translator.translate(language, "reorder", `WlReorder/${key}`);

  useEffect(() => {
    setWlReorderPage?.(true);
    return () => setWlReorderPage?.(false);
  }, []);

  return (
    <div data-testid="wl-reorder-plan-screen" className="mx-auto flex w-full max-w-[384px] flex-col">
      <div className="flex w-full flex-col gap-[32px]">
        <ReorderProductSummary
          imageSrc={`${product.base_path}/${product.name}`}
          title={product.title}
          dosage={product.dosage_str}
          quantity={product.custom_quantity_title}
        />

        <div role={showOneTimeOption ? "radiogroup" : undefined} className="flex w-full flex-col gap-[10px]">
          {showOneTimeOption && (
            <h2 className="m-0 text-center font-poppins text-[18px] font-semibold leading-[25px] text-black">{text("choose_title")}</h2>
          )}

          <ReorderCarePlusPlanCard
            language={language}
            country={country}
            currencyRate={currencyRate}
            pricing={pricing.plan}
            medicationPrice={medicationPrice}
            isTabletProduct={offer.showMedicationInPlanPrice}
            selectable={showOneTimeOption}
            isSelected={selectedOption === "subscription"}
            onSelect={() => setSelectedOption("subscription")}
          />
          {showOneTimeOption && (
            <ReorderOneTimeOptionCard
              language={language}
              country={country}
              currencyRate={currencyRate}
              pricing={pricing.oneTime}
              isSelected={selectedOption === "one-time"}
              onSelect={() => setSelectedOption("one-time")}
            />
          )}

          <div className="w-full pt-[24px]">
            <button
              id="reorder-submit"
              type="button"
              onClick={submit}
              disabled={submitting}
              className="flex h-[56px] w-full cursor-pointer items-center justify-center gap-[8px] rounded-[8px] border-0 py-[16px] transition-[background] duration-[350ms] hover:!bg-[linear-gradient(90deg,#e08600_0%,#f5a800_100%)] disabled:cursor-not-allowed disabled:opacity-60"
              style={{ background: CARE_PLUS_ORANGE_GRADIENT, boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)" }}
            >
              <span className="font-poppins text-[16px] font-semibold leading-[24px] text-white">{text("continue_to_shipping")}</span>
              <img src={arrowRightIcon} alt="" width="16" height="16" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center justify-between pt-[12px] font-roboto text-[12px] leading-[16px]">
        <span className="text-[#6b7a68]">{text("not_ready")}</span>
        <a
          className="font-medium text-[#61a333] underline"
          href={`/select_treatment/${country}/${language}/${reorderData.catalogMention}?${searchParams.toString()}`}
        >
          {text("different_treatment_link")}
        </a>
      </div>
    </div>
  );
};

export default WlReorderPlanScreen;
